"use client";

import { QueryClient, queryOptions } from "@tanstack/react-query";
import { Entity, getEntityList, getSchemaHash } from "@/services/entity.service";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { useCustomerId } from "@/contexts/customer-id-context";

// The hash changes whenever entityList would return something else for this user, whoever
// changed the schema (admin panel, API, MCP). Keying the list by it replaces invalidation.
export const schemaHashQuery = (customerId: string | null) =>
  queryOptions({
    queryKey: ["schemaHash", customerId],
    queryFn: getSchemaHash,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60 * 1000,
  });

export const entitiesQuery = (customerId: string | null, schemaHash: string | undefined) =>
  queryOptions({
    queryKey: ["entities", customerId, schemaHash],
    queryFn: getEntityList,
    staleTime: Infinity,
  });

export const useSchemaHash = () => {
  const customerId = useCustomerId();
  return useAuthenticatedQuery({ ...schemaHashQuery(customerId), enabled: !!customerId }).data;
};

/** Newest entity list cached for the customer under any hash, e.g. one restored from IndexedDB. */
export const getLatestCachedEntities = (queryClient: QueryClient, customerId: string | null) =>
  queryClient
    .getQueryCache()
    .findAll({ queryKey: ["entities", customerId] })
    .filter((query) => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0]?.state.data as
    | Entity[]
    | undefined;
